import axios from "axios";
import { invalidateDocument as invalidateDocumentQueries } from "@/lib/cache/invalidate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

function authHeaders() {
  const headers = { "Content-Type": "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      const onLogin = window.location.pathname === "/login";
      if (!onLogin) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export async function uploadDocument(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/documents/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
}

export async function listDocuments() {
  const res = await api.get("/documents");
  return res.data.documents || [];
}

export async function getDocument(id, { includeText = false } = {}) {
  const res = await api.get(`/documents/${id}`, {
    params: { include_text: includeText },
  });
  return res.data;
}

export async function getDocumentFileUrl(id) {
  const res = await api.get(`/documents/${id}/file-url`);
  return res.data;
}

export async function deleteDocument(id) {
  const res = await api.delete(`/documents/${id}`);
  await invalidateDocumentQueries(undefined, id);
  return res.data;
}

export async function listModels() {
  const res = await api.get("/models");
  return res.data;
}

export async function listConversations({
  documentId,
  documentIds,
  caseId,
  researchSessionId,
  includeMessages = true,
} = {}) {
  const params = { include_messages: includeMessages };
  if (caseId) {
    params.case_id = caseId;
  } else if (researchSessionId) {
    params.research_session_id = researchSessionId;
  } else if (documentIds?.length) {
    params.document_ids = documentIds.join(",");
  } else if (documentId) {
    params.document_id = documentId;
  }
  const res = await api.get("/conversations", { params });
  return res.data.conversations || [];
}

export async function getConversation(id) {
  const res = await api.get(`/conversations/${id}`);
  return res.data;
}

export async function deleteConversation(id) {
  const res = await api.delete(`/conversations/${id}`);
  return res.data;
}

/**
 * Stream a JSON-line SSE endpoint via fetch. Calls onChunk for every JSON event.
 * Returns a promise that resolves when stream completes.
 */
export async function streamEndpoint(path, body, onChunk, signal) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      const s = line.trim();
      if (!s.startsWith("data:")) continue;
      const payload = s.slice(5).trim();
      if (!payload) continue;
      try {
        const evt = JSON.parse(payload);
        onChunk(evt);
        if (evt.done) return;
      } catch {
        /* ignore */
      }
    }
  }
}

export async function searchDocuments(query, model, useAi = true) {
  const res = await api.post("/search", { query, model, use_ai: useAi });
  return res.data;
}

export async function unifiedResearch(query, model, sources, useAi = true) {
  const res = await api.post("/research", { query, model, sources, use_ai: useAi });
  return res.data;
}

export async function createResearchSession(query, model) {
  const res = await api.post(
    "/research/sessions",
    { query, model },
    { timeout: 300000 },
  );
  return res.data;
}

export async function listResearchSessions() {
  const res = await api.get("/research/sessions");
  return res.data.sessions || [];
}

export async function getResearchSession(id) {
  const res = await api.get(`/research/sessions/${id}`);
  return res.data;
}

export async function deleteResearchSession(id) {
  const res = await api.delete(`/research/sessions/${id}`);
  return res.data;
}

export async function searchQarark(query, options = {}) {
  const res = await api.post("/research/qarark/search", {
    query,
    page: options.page ?? 1,
    limit: options.limit ?? 20,
    content_type: options.content_type ?? "c3",
    number: options.number || undefined,
    year: options.year || undefined,
    gazette_number: options.gazette_number || undefined,
    leg_type: options.leg_type ?? "0",
    validity: options.validity ?? "x",
    court_number: options.court_number || undefined,
    court_year: options.court_year || undefined,
  });
  return res.data;
}

export async function saveQararkToLibrary(docId, title) {
  const res = await api.post("/research/qarark/save", { doc_id: docId, title });
  return res.data;
}

export async function saveQararkBatch(items) {
  const res = await api.post("/research/qarark/save-batch", {
    items: items.map((item) => ({ doc_id: item.doc_id, title: item.title })),
  });
  return res.data;
}

export async function listWorkflows(lang = "ar") {
  const res = await api.get("/workflows", { params: { lang } });
  return res.data;
}

export async function listChatPrompts(lang = "ar") {
  const res = await api.get("/chat/prompts", { params: { lang } });
  return res.data;
}

export async function getQararkStatus() {
  const res = await api.get("/integrations/qarark/status");
  return res.data;
}

export async function connectQarark({ username, password, captcha }) {
  const res = await api.post("/integrations/qarark/connect", {
    username,
    password,
    captcha,
  });
  return res.data;
}

export async function disconnectQarark() {
  const res = await api.post("/integrations/qarark/disconnect");
  return res.data;
}

export async function exportDraftDocx(content, title, language) {
  const res = await api.post(
    "/draft/export",
    { content, title, language },
    { responseType: "blob" },
  );
  return res.data;
}

export async function updateDraftDocument(docId, content, conversationId) {
  const res = await api.patch(`/draft/${docId}`, {
    content,
    conversation_id: conversationId || undefined,
  });
  return res.data;
}

export async function listCases() {
  const res = await api.get("/cases");
  return res.data;
}

export async function createCase(payload) {
  const res = await api.post("/cases", payload);
  return res.data;
}

export async function getCase(caseId) {
  const res = await api.get(`/cases/${caseId}`);
  return res.data;
}

export async function updateCase(caseId, payload) {
  const res = await api.patch(`/cases/${caseId}`, payload);
  return res.data;
}

export async function deleteCase(caseId) {
  const res = await api.delete(`/cases/${caseId}`);
  return res.data;
}

export async function linkCaseDocument(caseId, documentId, role = "reference") {
  const res = await api.post(`/cases/${caseId}/documents`, { document_id: documentId, role });
  return res.data;
}

export async function unlinkCaseDocument(caseId, documentId) {
  const res = await api.delete(`/cases/${caseId}/documents/${documentId}`);
  return res.data;
}

export async function uploadCaseDocument(caseId, file, role = "reference", onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post(`/cases/${caseId}/documents/upload?role=${encodeURIComponent(role)}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
}

export async function generateCaseQuestions(caseId, model) {
  const res = await api.post(`/cases/${caseId}/onboarding/questions`, { model });
  return res.data;
}

export async function submitCaseAnswers(caseId, answers, model) {
  const res = await api.post(`/cases/${caseId}/onboarding/answers`, { answers, model });
  return res.data;
}

export async function skipCaseOnboarding(caseId) {
  const res = await api.post(`/cases/${caseId}/onboarding/skip`);
  return res.data;
}

export async function getCaseDraftSuggestions(caseId, model, refresh = false) {
  const res = await api.post(`/cases/${caseId}/draft/suggestions`, { model, refresh });
  return res.data;
}

export async function suggestCaseQarark(payload) {
  const res = await api.post("/cases/qarark/suggest", payload);
  return res.data;
}

export async function importCaseQarark(caseId, items) {
  const res = await api.post(`/cases/${caseId}/qarark/import`, { items });
  return res.data;
}

export async function listDraftTypes() {
  const res = await api.get("/draft/types");
  return res.data;
}

export async function getMe() {
  const res = await api.get("/auth/me");
  return res.data;
}

export async function updateProfile(payload) {
  const res = await api.patch("/auth/me", payload);
  return res.data;
}

export async function listCountries() {
  const res = await api.get("/countries");
  return res.data;
}
